// build-photo-size-badges.js

import IU from '../../../_utils/img/img-utils';
import { aboveMinimumDpi } from './dpi-classification';

const DEFAULT_DPI = 300;

/**
 * Builds a map of badge types for a given set of photo instances against a list of available print sizes.
 *
 * Badge precedence is as follows:
 * 1. Low res if the original image is low DPI for this size ('raw_photo_low_res').
 * 2. User has cropped a photo with adequate DPI too small for this size ('cropped_too_small').
 * 3. Recommended print size, if applicable ('recommended').
 *
 * @param {Array} allAvailableSizes - The list of all possible print sizes.
 * @param {Array} allPhotoInstances - All photo instances for a single root image.
 * @param {Object} recommendedPrintSize - The recommended print size object for this group of photos.
 * @returns {Map<string, string|null>} - A map where keys are size keys and values are the badge type.
 */
export default function buildPhotoSizeBadges(
  allAvailableSizes,
  allPhotoInstances,
  recommendedPrintSize,
) {
  const badgeTypeMap = new Map();

  if (!allPhotoInstances || allPhotoInstances.length === 0) {
    return badgeTypeMap;
  }

  const rawDimensions = allPhotoInstances[0]?.raw_dimensions || null;

  allAvailableSizes.forEach((size) => {
    const { key: sizeKey, area } = size;
    let badgeType = null;

    // If the original photo dimensions are unknown then fallback to satisfactory DPI
    const originalDpi = rawDimensions
      ? IU.imgHelpers.getPhotoDPI({
          raw_dimensions: rawDimensions,
          size_area: area,
        })
      : DEFAULT_DPI;

    // Check if this size has any associated photos with a crop that results in low DPI
    const hasLowDpiCrop = allPhotoInstances.some((p) => {
      if (p.size.key !== sizeKey || !p.crop) return false;

      const croppedDpi = IU.imgHelpers.getPhotoDPI({
        size_area: p.size.area,
        raw_dimensions: p.raw_dimensions,
        crop: p.crop,
      });

      return !aboveMinimumDpi(croppedDpi);
    });

    if (rawDimensions && !aboveMinimumDpi(originalDpi)) {
      badgeType = 'raw_photo_low_res';
    } else if (hasLowDpiCrop) {
      badgeType = 'cropped_too_small';
    } else if (recommendedPrintSize && recommendedPrintSize.key === sizeKey) {
      badgeType = 'recommended';
    }

    badgeTypeMap.set(sizeKey, badgeType);
  });

  return badgeTypeMap;
}
